import { Loader } from '@googlemaps/js-api-loader'
import * as THREE from 'three'

const GoogleMapsWebGLPlugin = {
  install(Vue, options) {
    const apiOptions = {
      apiKey: options.apiKey,
      version: 'beta',
      libraries: ['marker', 'places'],
    }

    let animationFrameId = null

    Vue.prototype.$initMap = async function (
      elementId,
      mapOptions,
      markers = [],
      addCustomControls,
      mapTypeId = 'satellite',
      withMarker = true,
      currentLayout,
    ) {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
        animationFrameId = null
      }

      const mapDiv = document.getElementById(elementId)
      const apiLoader = new Loader(apiOptions)
      await apiLoader.load()

      const { AdvancedMarkerElement } = await google.maps.importLibrary('marker')

      mapOptions.mapTypeId = mapTypeId
      mapOptions.mapTypeControl = true
      mapOptions.zoomControl = false
      mapOptions.fullscreenControl = false

      const map = new google.maps.Map(mapDiv, {
        ...mapOptions,
        mapId: options.mapId || 'd3b3f166493c990b',
        tilt: mapOptions.tilt,
        heading: mapOptions.heading,
        zoom: mapOptions.zoom,
      })
      this.$map = map

      await initWebGLOverlayView(map, mapOptions)

      if (withMarker) {
        this.$markers = []
        markers.forEach((markerData) => {
          const marker = this.$addAdvancedMarker(markerData, map, AdvancedMarkerElement)
          this.$markers.push(marker)
        })
      }

      if (typeof addCustomControls === 'function') {
        addCustomControls(map, currentLayout)
      }

      // Adicionar funcionalidade de busca
      const input = document.createElement('input')
      input.type = 'text'
      input.placeholder = 'Buscar lugares...'
      input.style =
        'position: absolute; top: 10px; left: 10px; z-index: 5; width: 300px; padding: 10px;'

      map.controls[google.maps.ControlPosition.TOP_LEFT].push(input)

      const searchBox = new google.maps.places.SearchBox(input)
      map.addListener('bounds_changed', () => {
        searchBox.setBounds(map.getBounds())
      })

      searchBox.addListener('places_changed', () => {
        const places = searchBox.getPlaces()
        if (places.length == 0) {
          return
        }

        const bounds = new google.maps.LatLngBounds()
        places.forEach((place) => {
          if (!place.geometry || !place.geometry.location) {
            console.log('Returned place contains no geometry')
            return
          }

          if (place.geometry.viewport) {
            bounds.union(place.geometry.viewport)
          } else {
            bounds.extend(place.geometry.location)
          }
        })
        map.fitBounds(bounds)

        // Navegar até o local selecionado
        const firstPlace = places[0]
        if (firstPlace && firstPlace.geometry) {
          map.setCenter(firstPlace.geometry.location)
          map.setZoom(15) // Ajuste o nível de zoom conforme necessário
        }
      })

      this.$root.$emit('map-loaded') // Emitir evento de mapa carregado

      return map
    }

    Vue.prototype.$initTrafficMap = async function (
      elementId,
      mapOptions,
      markers = [],
      addCustomControls,
      mapTypeId,
      withMarker,
      currentLayout,
    ) {
      await this.$initMap(
        elementId,
        mapOptions,
        markers,
        addCustomControls,
        mapTypeId,
        withMarker,
        currentLayout,
      )
      this.$setTrafficLayer(true)
    }

    Vue.prototype.$initWeatherMap = async function (
      elementId,
      mapOptions,
      markers = [],
      addCustomControls,
      mapTypeId = 'roadmap',
      withMarker = true,
      currentLayout,
    ) {
      await this.$initMap(
        elementId,
        mapOptions,
        markers,
        addCustomControls,
        mapTypeId,
        withMarker,
        currentLayout,
      )
    }

    Vue.prototype.$updateMapCenter = function (lat, lng) {
      if (this.$map) {
        this.$map.setCenter({ lat, lng })
      }
    }

    Vue.prototype.$addAdvancedMarker = function (markerData, map, AdvancedMarkerElement) {
      const { lat, lng, title, type } = markerData
      const backgroundColorMarker =
        type === 'rodoviaria' ? '#7749F8' : type === 'aeroporto' ? '#279CBA' : '#158178'
      const markerElement = document.createElement('div')
      markerElement.className = 'custom-marker'
      markerElement.style.backgroundColor = backgroundColorMarker
      markerElement.style.width = '70px'
      markerElement.style.height = '70px'
      markerElement.style.borderRadius = '8px'
      markerElement.style.display = 'flex'
      markerElement.style.flexDirection = 'column'
      markerElement.style.alignItems = 'center'
      markerElement.style.justifyContent = 'center'
      markerElement.style.padding = '4px'
      markerElement.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)'
      markerElement.style.position = 'relative'
      markerElement.style.transition = 'transform 0.2s ease-in-out'

      const imgElement = document.createElement('div')
      imgElement.style.backgroundImage = `url(/images/${type}.png)`
      imgElement.style.width = '32px'
      imgElement.style.height = '32px'
      imgElement.style.backgroundSize = 'contain'
      imgElement.style.backgroundRepeat = 'no-repeat'
      imgElement.style.backgroundPosition = 'center'

      const button = document.createElement('button')
      button.innerHTML = 'Ver detalhes >'
      button.style.display = 'none'
      button.style.marginTop = '5px'
      button.style.padding = '2px'
      button.style.fontSize = '0.55rem'
      button.style.fontWeight = '600'
      button.style.backgroundColor = '#FFFFFF'
      button.style.color = '#37434A'
      button.style.border = 'none'
      button.style.cursor = 'pointer'
      button.style.borderRadius = '4px'

      markerElement.appendChild(imgElement)
      markerElement.appendChild(button)

      markerElement.addEventListener('mouseover', () => {
        markerElement.style.transform = 'scale(1.2)'
        button.style.display = 'block'
      })

      markerElement.addEventListener('mouseout', () => {
        markerElement.style.transform = 'scale(1)'
        button.style.display = 'none'
      })

      button.addEventListener('click', () => {
        this.$root.$emit('marker-click', markerData)
      })

      const marker = new AdvancedMarkerElement({
        position: { lat, lng },
        map,
        title,
        content: markerElement,
      })

      marker.addEventListener('gmp-click', () => {
        this.$root.$emit('marker-click', markerData)
      })

      return marker
    }

    Vue.prototype.$setTrafficLayer = function (enabled) {
      if (this.$map) {
        if (enabled) {
          this.$trafficLayer = new google.maps.TrafficLayer()
          this.$trafficLayer.setMap(this.$map)
        } else if (this.$trafficLayer) {
          this.$trafficLayer.setMap(null)
        }
      }
    }

    async function initWebGLOverlayView(map, mapOptions) {
      return new Promise((resolve, reject) => {
        let scene, renderer, camera
        const webGLOverlayView = new google.maps.WebGLOverlayView()

        webGLOverlayView.onAdd = () => {
          scene = new THREE.Scene()
          camera = new THREE.PerspectiveCamera()
          const ambientLight = new THREE.AmbientLight(0xffffff, 0.75)
          scene.add(ambientLight)
          const directionalLight = new THREE.DirectionalLight(0xffffff, 0.25)
          directionalLight.position.set(0.5, -1, 0.5)
          scene.add(directionalLight)
        }

        webGLOverlayView.onContextRestored = ({ gl }) => {
          renderer = new THREE.WebGLRenderer({
            canvas: gl.canvas,
            context: gl,
            ...gl.getContextAttributes(),
          })

          renderer.autoClear = false

          const animate = () => {
            animationFrameId = requestAnimationFrame(animate)
            map.moveCamera({
              tilt: mapOptions.tilt,
              heading: mapOptions.heading,
              zoom: mapOptions.zoom,
            })

            if (mapOptions.tilt < 67.5) {
              mapOptions.tilt += 0.5
            } else if (mapOptions.heading <= 360) {
              mapOptions.heading += 0.2
            } else {
              cancelAnimationFrame(animationFrameId)
            }
            renderer.render(scene, camera)
          }
          animate()
          resolve()
        }

        webGLOverlayView.onDraw = ({ gl, transformer }) => {
          webGLOverlayView.requestRedraw()
          renderer.render(scene, camera)
          renderer.resetState()
        }

        map.addListener('mousedown', () => cancelAnimationFrame(animationFrameId))
        map.addListener('touchstart', () => cancelAnimationFrame(animationFrameId))

        webGLOverlayView.setMap(map)
      })
    }
  },
}

export default GoogleMapsWebGLPlugin
